// Calendário pequeno do "Escolher data" (AC-213): o mês em grade, setas para os outros
// meses, dias passados apagados (ASM-106). Abre numa camada por cima de tudo, junto do chip,
// sempre inteiro dentro da janela; fecha ao escolher, com Esc ou clicando fora.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { parseDateKey, toDateKey, type DateKey } from '../domain/dates';
import { Icon } from './Icon';
import { cls } from './NewTaskForm';

/** A semana começa no domingo em pt-BR e en; na segunda nos outros idiomas (ASM-106). */
export function weekStartsOn(language: string): 0 | 1 {
  return language === 'pt-BR' || language === 'en' ? 0 : 1;
}

/** Dias da grade do mês (null = espaço antes do dia 1). */
export function monthGrid(year: number, month: number, firstDay: 0 | 1): Array<DateKey | null> {
  const lead = (new Date(year, month, 1).getDay() - firstDay + 7) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells: Array<DateKey | null> = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= days; d++) cells.push(toDateKey(new Date(year, month, d)));
  return cells;
}

type Box = { top: number; left: number; bottom: number; right: number };

/**
 * Onde abrir o calendário: embaixo do chip, alinhado à esquerda dele; sem espaço embaixo,
 * abre em cima; e nunca sai da janela (margem de 8 px) — nada fica cortado.
 */
export function placePopover(
  anchor: Box,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = 8,
  gap = 6
): { top: number; left: number } {
  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));
  const left = clamp(anchor.left, margin, viewport.width - size.width - margin);
  const below = anchor.bottom + gap;
  const above = anchor.top - gap - size.height;
  const top = below + size.height <= viewport.height - margin || above < margin ? below : above;
  return { top: clamp(top, margin, viewport.height - size.height - margin), left };
}

interface Props {
  value: DateKey | null;
  /** primeiro dia que pode ser escolhido (hoje) */
  min: DateKey;
  /** o chip que abre o calendário (posição e clique "de dentro") */
  anchor: HTMLElement | null;
  onPick(date: DateKey): void;
  onClose(): void;
}

export function Calendar({ value, min, anchor, onPick, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const start = parseDateKey(value ?? min);
  const [view, setView] = useState({ year: start.getFullYear(), month: start.getMonth() });
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // posiciona junto do chip, inteiro dentro da janela — e de novo ao rolar ou redimensionar
  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el || !anchor) return;
      const a = anchor.getBoundingClientRect();
      const c = el.getBoundingClientRect();
      setPos(placePopover(a, { width: c.width, height: c.height }, { width: window.innerWidth, height: window.innerHeight }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, view]);

  useEffect(() => {
    // clicar no chip não conta como "fora" (senão fecharia e reabriria no mesmo clique)
    const outside = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !anchor?.contains(target)) close.current();
    };
    document.addEventListener('pointerdown', outside, true);
    // abre com o foco no dia marcado (ou hoje): dá para seguir pelo teclado
    ref.current?.querySelector<HTMLElement>('.calendar__day.is-on, .calendar__day.is-today, .calendar__day:not(:disabled)')?.focus();
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [anchor]);

  const firstDay = weekStartsOn(lang);
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(lang, { weekday: 'narrow' }).format(new Date(2026, 1, 1 + ((i + firstDay) % 7)))
  );
  // só a primeira letra maiúscula ("Outubro de 2026", não "Outubro De 2026")
  const monthYear = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(new Date(view.year, view.month, 1));
  const title = monthYear.charAt(0).toLocaleUpperCase(lang) + monthYear.slice(1);
  const longDate = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', year: 'numeric' });
  const today = min;
  const move = (delta: number) => {
    const d = new Date(view.year, view.month + delta, 1);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };
  const minMonth = parseDateKey(min);
  const atFirstMonth = view.year === minMonth.getFullYear() && view.month === minMonth.getMonth();

  // camada própria (portal): não fica cortado pela caixa do formulário nem pela janela
  return createPortal(
    <div
      ref={ref}
      className="calendar"
      role="dialog"
      aria-label={t('newTask.pickDate')}
      data-testid="calendar"
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
    >
      <div className="calendar__head">
        <button type="button" className="icon-btn icon-btn--sm" aria-label={t('calendar.prev')} disabled={atFirstMonth} onClick={() => move(-1)}>
          <Icon name="chevronLeft" size={16} />
        </button>
        <span className="calendar__title">{title}</span>
        <button type="button" className="icon-btn icon-btn--sm" aria-label={t('calendar.next')} onClick={() => move(1)}>
          <Icon name="chevronRight" size={16} />
        </button>
      </div>
      <div className="calendar__grid" role="grid">
        {weekdays.map((w, i) => (
          <span key={`w${i}`} className="calendar__weekday" aria-hidden>
            {w}
          </span>
        ))}
        {monthGrid(view.year, view.month, firstDay).map((day, i) =>
          day ? (
            <button
              key={day}
              type="button"
              className={cls('calendar__day', day === value && 'is-on', day === today && 'is-today')}
              aria-label={longDate.format(parseDateKey(day))}
              aria-pressed={day === value}
              // dias que já passaram não podem ser escolhidos — o da tarefa atrasada continua marcado
              disabled={day < min && day !== value}
              onClick={() => onPick(day)}
            >
              {parseDateKey(day).getDate()}
            </button>
          ) : (
            <span key={`e${i}`} />
          )
        )}
      </div>
    </div>,
    document.body
  );
}
